import { Checkbox, Tooltip, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import type { TimeSession } from '@worklogix/shared';
import { Fragment, useMemo } from 'react';

import { MOBILE } from '../../responsive.js';
import { deriveSessionFlags, type SessionFlag } from '../flags.js';
import { postOutcomeBadge } from '../postOutcome.js';
import type { EmployeeBlock } from '../timecardGrouping.js';

/**
 * The payroll week as a CALENDAR rather than a list (Michael, 2026-09-16: "We need
 * the time entries to look like a calendar and be easier to track and edit").
 *
 * The shape is ADI's — employees down, days across — because that is the view the
 * office already reads.
 *
 * 🔥 ONE CARD PER EMPLOYEE-DAY, NOT ONE CARD PER ENTRY (Michael, 2026-09-30).
 *
 * "My people say it is difficult to understand the calendar as it stands now,
 * especially for the workers that work part of the day, clock out, then clock back in
 * that same day and work that night into the next day ... I want to try making it so
 * that the cards showcase all time entries for a day on one card."
 *
 * The day this replaces rendered as three unrelated objects — an afternoon shift, an
 * evening one, and a separate card floating across the next two columns for the part
 * after midnight — and a reviewer had to reassemble the person's day in their head
 * before they could judge any of it. What matters about a split day is the SHAPE OF
 * THE WHOLE DAY: total hours, where the gaps fell, which department each stretch
 * billed to. That is one question, so it is one card.
 *
 * GROUPED BY CLOCK-IN DAY. An overnight entry belongs to the evening it started, which
 * is how the person who worked it describes it and how the supervisor who was there
 * remembers it. It also retires the absolutely-positioned spanning overlay this view
 * used to need, and with it a whole family of bugs about spanning cards landing on top
 * of the entries underneath them.
 *
 * COLOUR MEANS STATUS, AND IT LIVES ON THE BARS. The card is white. Michael: "The
 * approve checkbox/status at the top of each card needs to go away and the cards
 * should all be white. The bars representing the individual times should be the thing
 * that changes color ... (this way people wont get confused if one entry is approved
 * while another has a flag and needs to be reviewed before it can be approved)". A
 * card painted one colour has to pick a winner between its entries, and whichever it
 * picks is a lie about the others.
 *
 * SELECTION IS PER ENTRY, NOT PER DAY (Michael, 2026-09-30, second pass: "I need you
 * to put in a checkbox next to each time entry on a card so that people can just go
 * through and check the entries one by one ... also build in a 'select all entries on
 * this card'").
 *
 * 🔥 THIS IS NOT THE CHECKBOX THAT WAS REMOVED. The old one sat at the TOP of the
 * card, above everything, and a card is a whole day — so one tick could only ever have
 * meant "approve all of it", which is the same conflation as painting the card one
 * colour. These sit ON the time rows, one per entry, so ticking the 4:03p stretch and
 * leaving the flagged 10:33p one is expressible. The card-level box is an accelerator
 * for the rows beneath it and says exactly how many it covers.
 */

/** Minutes in a day — the denominator for every horizontal position here. */
const MINUTES_PER_DAY = 1440;

/**
 * A shift longer than this is worth a second look.
 *
 * Matches the 13-hour long-shift alert the API already sends, so the calendar and the
 * email agree about what "too long" means — and it is the figure Michael set for that
 * alert, not one invented here. It is the ONLY thing that turns an entry red: a shift
 * crossing midnight is drawn running to the edge of its day but is not, by itself,
 * suspect (Michael, 2026-09-18: "Since we have night workers, there can definitely be
 * time entries that span two days. The flag needs to be based more on total length of
 * the time entry instead of how many dates the entry contains").
 */
const IMPLAUSIBLE_HOURS = 13;

/**
 * The width one entry checkbox occupies, and the width reserved for an entry that has
 * none. ONE constant for both — see `checkSpacer`.
 */
const CHECK_WIDTH = 20;

/** Where an entry has got to. Drives the bar colour and the card's faint wash. */
type EntryStatus = 'posted' | 'approved' | 'pending' | 'open' | 'suspect';

export interface WeekCalendarProps {
  /** Employee blocks, already filtered and ordered by the parent. */
  blocks: readonly EmployeeBlock[];
  /** The seven days of the payroll week, `YYYY-MM-DD`, Wed→Tue. */
  days: readonly string[];
  /** Supervisor display name by employee id — Michael's #6. */
  supervisorNameByEmployee: ReadonlyMap<string, string>;
  /**
   * Department display name by department id.
   *
   * 🔥 NOT OPTIONAL, deliberately. The calendar replaces the list, and the list
   * carried a department column. Department is the field most likely to be WRONG --
   * it is the whole of Michael's 2026-09-17 #5 ("We had a lot of employees who logged
   * in under the wrong kiosk this week") -- and it decides which earning type the
   * hours post under. A calendar that cannot show it would hide the error it exists
   * to surface.
   */
  departmentNameById: ReadonlyMap<string, string>;
  /**
   * sessionId → "NAME is working on this entry", for entries somebody else has open.
   *
   * Shown as a dot on the card so it is visible BEFORE anyone opens the entry; the
   * full sentence lands in the dialog, where a second person is actually about to type
   * over somebody's work.
   */
  presenceBySession?: ReadonlyMap<string, string>;
  /**
   * Raised when a card is clicked.
   *
   * BOTH ARGUMENTS MATTER. The second is every entry on that card, in the order they
   * are drawn, so the dialog can page between them ("Entry 1", "Entry 2") instead of
   * making somebody close it and hunt for the next stretch of the same day.
   */
  onSelect?: (session: TimeSession, daySessions: readonly TimeSession[]) => void;
  /** Ids the reviewer has ticked, for the bulk-approve action in the toolbar. */
  selectedIds?: ReadonlySet<string>;
  /** Toggle one id. Absent = the calendar renders no checkboxes at all. */
  onToggleSelect?: (id: string) => void;
  /**
   * Set MANY ids at once — what the card-level box uses.
   *
   * A separate callback rather than a loop over `onToggleSelect`, because a loop of
   * setState calls over a day's entries is both N renders and, worse, a toggle: a card
   * holding one ticked and two unticked entries would flip each one and end up with the
   * opposite selection rather than all three. This states the intent instead.
   */
  onSetSelected?: (ids: readonly string[], selected: boolean) => void;
  /** Whether this session can be approved (closed, unapproved, not frozen). */
  isApprovable?: (session: TimeSession) => boolean;
}

/** One entry drawn inside a day card. */
export interface EntryBar {
  session: TimeSession;
  /** Position within its card's entry list — what the dialog calls "Entry N". */
  ordinal: number;
  /** 0–1 across the card's 24h day: where the bar begins. */
  offset: number;
  /** 0–1 of the day: how much of it the bar covers, clamped at midnight. */
  width: number;
  hours: number;
  open: boolean;
  suspect: boolean;
  /** Whole days between the clock-in day and the clock-out. 0 = ended the same day. */
  endsDaysLater: number;
  status: EntryStatus;
}

/** One employee-day: every entry that STARTED that day, and what they add up to. */
export interface DayCard {
  dayIndex: number;
  entries: EntryBar[];
  totalHours: number;
  /** Hours per department, present only when the day touched more than one. */
  deptSplit: ReadonlyArray<{ name: string; hours: number }>;
  /** The single department name, when the whole day billed to one. */
  soleDeptName: string | null;
  flags: CardFlag[];
}

/** One flag as the card shows it: deduplicated, and told which entries it concerns. */
export interface CardFlag {
  key: string;
  label: string;
  description: string;
  severity: SessionFlag['severity'] | 'presence' | 'success';
  /** Ordinals of the entries carrying it. */
  entries: number[];
}

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    // Name column, then one equal column per day. minmax(0,1fr) so a wide card cannot
    // stretch its column and knock the week out of alignment.
    // The whole week FITS (Michael, 2026-09-17: "The scroll bar is hideous. You need
    // to find a way to cleanly show all days in the week").
    gridTemplateColumns: 'minmax(130px, 170px) repeat(7, minmax(0, 1fr))',
    /*
     * 🔥 SEVEN COLUMNS DO NOT FIT ON A PHONE. Below the breakpoint each day would be
     * roughly 30px wide -- narrower than the times written in it -- so the calendar
     * hides and `HoursTimesheet` renders its per-shift cards instead.
     *
     * This is the failure mode worth naming: the grid does not error or overflow
     * visibly at that width, it just renders something unreadable.
     */
    [MOBILE]: { display: 'none' },
    ...shorthands.gap('1px'),
    backgroundColor: tokens.colorNeutralStroke2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  headCell: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    ...shorthands.padding('10px', '8px'),
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  headWeekend: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
  },
  nameCell: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding('10px'),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('3px'),
    minHeight: '92px',
    // Top-aligned, not centred: the cell carries a variable-length department list,
    // and centring made the employee's name drift down the taller the split got.
    justifyContent: 'flex-start',
    ...shorthands.borderLeft('3px', 'solid', tokens.colorBrandStroke1),
  },
  empName: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  supervisor: { fontSize: tokens.fontSizeBase200, color: tokens.colorBrandForeground2 },
  /**
   * The WEEK's hours split by department (Michael, 2026-09-24: "This split allocation
   * allows us to quickly see if there are any problems before having to open each
   * individual entry"). The per-DAY split now lives on each card; this stays the
   * week's total, beside the employee it belongs to.
   */
  deptSplit: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('1px'),
    ...shorthands.margin('4px', '0', '0', '0'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    paddingTop: '4px',
  },
  deptRow: {
    display: 'flex',
    justifyContent: 'space-between',
    ...shorthands.gap('6px'),
    fontSize: tokens.fontSizeBase100,
    lineHeight: '15px',
  },
  deptName: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  deptHours: { flexShrink: 0, fontVariantNumeric: 'tabular-nums' },
  /** Unapproved hours are the ones somebody still has to act on, so they carry. */
  deptUnapproved: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  deptTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    ...shorthands.gap('6px'),
    fontSize: tokens.fontSizeBase100,
    lineHeight: '16px',
    fontWeight: tokens.fontWeightSemibold,
    marginTop: '2px',
  },
  /**
   * One employee-day. A FLEX COLUMN, so the single card inside can stretch to the
   * height of the row.
   *
   * 🔥 THE ROW HEIGHT IS ALREADY THE TALLEST CELL — that is what a CSS grid row
   * does, and every cell already stretched to it. What did NOT stretch was the CARD
   * inside the cell, so a Monday with one entry sat as a short card floating in a tall
   * cell next to a Wednesday with three. Michael, 2026-09-30: "you need to make all of
   * the cards for one person the same height. The height for the row should be
   * determined by the card with the most entry times."
   *
   * So nothing here computes a height. The card is told to fill what the grid already
   * decided, which means an employee who gains a fourth entry on one day re-sizes their
   * whole week on its own — no measurement to go stale, which is the failure this
   * component has had twice before with hand-maintained pixel arithmetic.
   */
  dayCell: {
    backgroundColor: tokens.colorNeutralBackground1,
    minHeight: '92px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  weekendCell: { backgroundColor: tokens.colorNeutralBackground3 },

  /**
   * The day card. WHITE, with the status wash applied inline as a gradient.
   *
   * Michael: "the cards should all be white ... If you want to add color to the cards,
   * maybe do an extremely faint background color and have it kind of blend with each
   * status of all the entries the card contains — so if a card has two entries and one
   * entry is approved while another isn't, have the faint background be green and fade
   * into blue halfway through the card."
   *
   * The wash is therefore built per card in {@link washFor} rather than living here:
   * the number of colour stops depends on how many entries the day holds, which no
   * static stylesheet can express.
   */
  card: {
    // Grows into whatever height the grid row settled on (see `dayCell`).
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    // A real bottom margin now that the card reaches the bottom of its cell; it used
    // to be 0 because the card ended wherever its content did.
    ...shorthands.margin('4px'),
    ...shorthands.padding('6px'),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    ':hover': { ...shorthands.borderColor(tokens.colorBrandStroke1) },
  },
  /**
   * The card-level "tick everything here" box, RIDING THE FIRST TIME ROW.
   *
   * 🔥 It had a row of its own for about an hour. Michael: "The all checkbox needs to
   * be on the same line as the first time entry times. It looks rediculous like this."
   * He is right — a card is ~92px tall and that spent a whole line, a ninth of the
   * card, on a control with nothing beside it, and pushed the times down so the thing
   * the card exists to show no longer started at the top.
   *
   * `marginLeft: auto` parks it at the right end of a row it shares, so it costs no
   * height at all. It shrinks last, and the time text beside it truncates first — the
   * times are what the row is for.
   *
   * RENDERED ONLY WHEN IT WOULD DO SOMETHING — two or more approvable entries. On the
   * ordinary one-shift day it would be the box beside it wearing a hat.
   */
  selectAllInline: {
    marginLeft: 'auto',
    flexShrink: 0,
    paddingLeft: '4px',
  },
  /** The times, one row per entry, at the very top of the card (Michael's 1.iii). */
  timeRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('2px'),
    fontSize: tokens.fontSizeBase200,
    lineHeight: '17px',
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
  },
  /** The time text itself — truncates, while the checkbox beside it never shrinks. */
  timeText: {
    minWidth: 0,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    cursor: 'pointer',
  },
  /**
   * A day column is ~150px, and Fluent's Checkbox ships with padding plus a 32px
   * minimum hit target — a fifth of the column, and enough to push the time text off
   * the end of the row it belongs to. Compressed here rather than by reaching for a
   * smaller control, so the thing people click stays a real target.
   *
   * 🔥 THE `&` IS LOAD-BEARING. Written as `'> label'` this silently does nothing:
   * Griffel needs an explicit parent selector, and the malformed version produced no
   * CSS at all and no error — the checkbox kept its full 32px and the rows came out
   * misaligned by exactly the amount the rule was meant to remove. Measured after.
   */
  check: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    width: `${String(CHECK_WIDTH)}px`,
    '& > label': {
      paddingLeft: 0,
      paddingRight: 0,
      minHeight: 'unset',
      ...shorthands.gap('0'),
    },
    '& .fui-Checkbox__indicator': { marginLeft: 0, marginRight: 0 },
  },
  /** Same compression, but free to be as wide as the word "All" beside it. */
  checkAll: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    '& > label': { paddingLeft: 0, paddingRight: 0, minHeight: 'unset' },
    '& .fui-Checkbox__indicator': { marginLeft: 0, marginRight: '2px' },
    '& .fui-Checkbox__label': { paddingLeft: 0, fontSize: tokens.fontSizeBase100 },
  },
  /**
   * Keeps the time text of an entry that CANNOT be ticked (posted, already approved,
   * still running) aligned with the ones that can. Without it a day holding one of each
   * reads as ragged and the eye stops trusting the column of boxes — which is the
   * column somebody is about to work down ticking.
   *
   * Shares {@link CHECK_WIDTH} with the real checkbox so the two cannot drift; they
   * drifted by 14px the first time, with the width written out separately in each.
   */
  checkSpacer: { flexShrink: 0, width: `${String(CHECK_WIDTH)}px` },
  /** The one entry that is probably a missed clock-out still reads loudly. */
  timeRowSuspect: { color: tokens.colorPaletteRedForeground1 },
  timeRowOpen: { color: tokens.colorPaletteYellowForeground2 },
  /** The combined total for the day (Michael's 1.iv), directly under the times. */
  totalRow: {
    display: 'flex',
    alignItems: 'center',
    fontSize: tokens.fontSizeBase200,
    lineHeight: '16px',
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
    ...shorthands.overflow('hidden'),
  },
  /** The hours themselves — truncate before the "All" box beside them does. */
  totalText: {
    minWidth: 0,
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  /** Per-department breakdown, only when the day touched more than one. */
  cardDeptRow: {
    display: 'flex',
    justifyContent: 'space-between',
    ...shorthands.gap('6px'),
    fontSize: tokens.fontSizeBase100,
    lineHeight: '14px',
    color: tokens.colorNeutralForeground3,
  },
  cardDeptName: {
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  cardDeptHours: { flexShrink: 0, fontVariantNumeric: 'tabular-nums' },
  /** The single department, when there is only one — same place, no number. */
  soleDept: {
    fontSize: tokens.fontSizeBase100,
    lineHeight: '15px',
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  /**
   * The slack between the header and the bottom of the card.
   *
   * Michael: "All excess space for the cards that don't have as many time entries
   * should go between the department and the visual time bar - the bar hugs the bottom
   * and everything else is the 'header' for the card."
   *
   * So the times, the total, the department split — the header — stay packed at the
   * top where they are read, this absorbs the difference, and the dots and the bar sit
   * on the bottom edge. Across a week that puts every employee's bars on ONE baseline,
   * which is what makes a row of them comparable at a glance instead of a stagger.
   *
   * The dots ride with the bar rather than staying in the header: they have been
   * "bottom right" since they were introduced (1.v), and they annotate the entries the
   * bar draws.
   */
  cardSpacer: { flexGrow: 1, minHeight: '4px' },
  /** The flag dots, bottom-right (Michael's 1.v). */
  dots: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    ...shorthands.gap('3px'),
    minHeight: '11px',
    marginTop: '3px',
  },
  /**
   * One flag = one dot, however many entries carry it.
   *
   * A DOT RATHER THAN A BADGE because a badge reading "No earning type" is wider than
   * the day column it would sit in. The dot carries severity by colour and the full
   * sentence in its tooltip — the information is not reduced, only the space it takes.
   *
   * 🔥 THE DOT IS ITS OWN TOOLTIP TRIGGER AND NOTHING WRAPS THE CARD, which is the fix
   * for Michael's twice-reported bug ("The little circles ... need hoverability to see
   * what flag each dot is for", 2026-09-24 and again 2026-09-30). A tooltip whose
   * trigger was the whole card covered every dot inside it: hovering a dot raised the
   * CARD's tooltip and the dot's never got a look in. Nothing on this card takes a
   * tooltip now except the dots and a truncated department name.
   *
   * It is also FOCUSABLE. A dot reachable only by hover is unreachable by keyboard and
   * on a touch screen, and this is the only place the reason for a flag is written.
   */
  dot: {
    width: '9px',
    height: '9px',
    ...shorthands.borderRadius('50%'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    display: 'inline-block',
    ':focus-visible': {
      ...shorthands.outline('2px', 'solid', tokens.colorStrokeFocus2),
      outlineOffset: '1px',
    },
  },
  dotDanger: {
    backgroundColor: tokens.colorPaletteRedForeground1,
    ...shorthands.borderColor(tokens.colorPaletteRedForeground1),
  },
  dotWarning: {
    backgroundColor: tokens.colorPaletteYellowForeground1,
    ...shorthands.borderColor(tokens.colorPaletteYellowForeground1),
  },
  dotImportant: {
    backgroundColor: tokens.colorNeutralForeground3,
    ...shorthands.borderColor(tokens.colorNeutralForeground3),
  },
  /** Somebody else is in this entry. Brand colour: a person, not a fault. */
  dotPresence: {
    backgroundColor: tokens.colorBrandForeground1,
    ...shorthands.borderColor(tokens.colorBrandForeground1),
  },
  dotSuccess: {
    backgroundColor: tokens.colorPaletteGreenForeground1,
    ...shorthands.borderColor(tokens.colorPaletteGreenForeground1),
  },

  /**
   * The day's timeline: ONE 24-hour track carrying ONE FILL PER ENTRY.
   *
   * Michael: "The bar at the bottom of the card should hold multiple bars with spaces
   * in between to showcase when the worker clocked out for lunch or something." The
   * gaps are the point — an unbroken stretch and two stretches an hour apart are
   * different days, and this drawing is the only thing that tells them apart at a
   * glance.
   *
   * The quarter-day ticks (midnight, 6am, noon, 6pm) turn the track into a visible
   * ruler, so a fill reads as a position and a length rather than a mark. Without them
   * a six-minute shift is a dot on a grey bar and means nothing (Michael, 2026-09-17:
   * "nobody can tell that the blue dot is meant to represent the span of time").
   */
  strip: {
    position: 'relative',
    height: '9px',
    marginTop: '5px',
    backgroundColor: tokens.colorNeutralBackground5,
    ...shorthands.borderRadius('3px'),
    backgroundImage: `repeating-linear-gradient(to right, ${tokens.colorNeutralStroke1} 0 1px, transparent 1px 25%)`,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  stripFill: {
    position: 'absolute',
    top: '0',
    height: '9px',
    ...shorthands.borderRadius('3px'),
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    ...shorthands.gap('14px'),
    ...shorthands.padding('0', '2px', '8px', '2px'),
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  legendItem: { display: 'flex', alignItems: 'center', ...shorthands.gap('5px') },
  swatch: {
    width: '18px',
    height: '9px',
    ...shorthands.borderRadius('3px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  legendStrip: {
    position: 'relative',
    width: '70px',
    height: '9px',
    backgroundColor: tokens.colorNeutralBackground5,
    ...shorthands.borderRadius('3px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundImage: `repeating-linear-gradient(to right, ${tokens.colorNeutralStroke1} 0 1px, transparent 1px 25%)`,
  },
  empty: {
    ...shorthands.padding('24px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

/** Local calendar day of an ISO instant, as `YYYY-MM-DD`. */
function dayKey(iso: string): string {
  const d = new Date(iso);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const date = String(d.getDate()).padStart(2, '0');
  return `${String(d.getFullYear())}-${month}-${date}`;
}

/** Minutes past local midnight. */
function minutesIntoDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

function hoursBetween(startIso: string, endIso: string): number {
  return (Date.parse(endIso) - Date.parse(startIso)) / 3_600_000;
}

/** Whole calendar days between two `YYYY-MM-DD` keys. Noon-anchored, so DST cannot slip it. */
function daysBetweenKeys(from: string, to: string): number {
  const a = Date.parse(`${from}T12:00:00`);
  const b = Date.parse(`${to}T12:00:00`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return 0;
  }
  return Math.round((b - a) / 86_400_000);
}

/**
 * `6:59a` — compact on purpose.
 *
 * A day column is about 150px on a real screen, and "6:59 AM–12:05 PM" does not fit in
 * that; truncated to "6:59 AM–..." the card stops answering the question it exists for.
 * Dropping the space and the second letter of the meridiem buys the ~40px that makes
 * the whole range readable.
 */
function shortTime(iso: string): string {
  const d = new Date(iso);
  const hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const suffix = hours < 12 ? 'a' : 'p';
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(display)}:${minutes}${suffix}`;
}

function statusOf(session: TimeSession, open: boolean, suspect: boolean): EntryStatus {
  if (suspect) {
    return 'suspect';
  }
  if (open) {
    return 'open';
  }
  if (session.postedToNs) {
    return 'posted';
  }
  return session.approvedAt !== undefined && session.approvedAt !== '' ? 'approved' : 'pending';
}

/**
 * The solid colour a bar takes for each status — the ONE place status becomes colour.
 *
 * The legend below reads from this same table, so a legend that disagrees with the
 * cards is not expressible. That went wrong once already, when the cards gained a
 * state the legend did not know about.
 */
const BAR_COLOR: Record<EntryStatus, string> = {
  posted: tokens.colorPaletteGreenForeground1,
  approved: tokens.colorPaletteGreenBorder1,
  pending: tokens.colorBrandBackground,
  open: tokens.colorPaletteYellowBorder1,
  suspect: tokens.colorPaletteRedBorder1,
};

/**
 * The faint tint the same status contributes to the card's wash.
 *
 * 🔥 MIXED DOWN TO A FEW PERCENT, not used at token strength. Michael asked for
 * cards that "should all be white" with "an extremely faint background color", and the
 * first attempt at this used Fluent's `*Background2` tints directly — which produced
 * solidly green and solidly pink cards, exactly the "card painted one colour" this
 * redesign exists to get away from. Colour is supposed to live on the BARS.
 *
 * `color-mix` toward `colorNeutralBackground1` rather than toward literal white, so the
 * wash follows the theme instead of turning a dark-mode card into a light smear.
 */
const WASH_STRENGTH = '9%';
const wash = (color: string): string =>
  `color-mix(in srgb, ${color} ${WASH_STRENGTH}, ${tokens.colorNeutralBackground1})`;

const WASH_COLOR: Record<EntryStatus, string> = {
  posted: wash(tokens.colorPaletteGreenForeground1),
  approved: wash(tokens.colorPaletteGreenBorder1),
  pending: wash(tokens.colorBrandBackground),
  open: wash(tokens.colorPaletteYellowBorder1),
  // The one status allowed to be a little louder: a probable missed clock-out is the
  // case this whole view was built to catch, and it still has to be the first thing
  // the eye lands on across a week of otherwise white cards.
  suspect: `color-mix(in srgb, ${tokens.colorPaletteRedBorder1} 16%, ${tokens.colorNeutralBackground1})`,
};

/**
 * The card's background: one faint band per entry, blending into the next.
 *
 * Michael: "have the faint background be green and fade into blue halfway through the
 * card." Each band is anchored at the CENTRE of its share of the width and the
 * gradient interpolates across the boundaries, which is what makes it read as a blend
 * rather than as stripes. The first and last are pinned to the edges so no colour is
 * cut off.
 *
 * A SINGLE-ENTRY DAY GETS A FLAT WASH rather than a gradient to itself: one shift on a
 * day is the common case and should read as plainly as possible.
 */
function washFor(entries: readonly EntryBar[]): string {
  const first = entries[0];
  if (first === undefined) {
    return tokens.colorNeutralBackground1;
  }
  if (entries.length === 1) {
    return `linear-gradient(${WASH_COLOR[first.status]}, ${WASH_COLOR[first.status]})`;
  }
  const step = 100 / entries.length;
  const stops = entries.map((e, i) => {
    const centre = i === 0 ? 0 : i === entries.length - 1 ? 100 : step * i + step / 2;
    return `${WASH_COLOR[e.status]} ${centre.toFixed(1)}%`;
  });
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

/**
 * Turn one session into a bar positioned inside its CLOCK-IN day.
 *
 * An OPEN session is drawn from its clock-in to the end of that day rather than given
 * a width of nothing: a shift nobody closed is the thing most worth seeing, and a
 * zero-width bar is invisible. It stops at midnight rather than running on (Michael,
 * 2026-09-17: "open time entries should not flow all the way through the rest of the
 * week") — one person still on the clock should not look like a company-wide outage.
 */
export function entryBar(session: TimeSession, ordinal: number): EntryBar {
  const startKey = dayKey(session.clockInAt);
  const open = session.clockOutAt === undefined || session.clockOutAt === '';
  const startMin = minutesIntoDay(session.clockInAt);

  const endsDaysLater = open ? 0 : daysBetweenKeys(startKey, dayKey(session.clockOutAt as string));
  // Minutes from the START OF THE CLOCK-IN DAY to the clock-out, which is past 1440
  // whenever the shift crossed midnight.
  const endMin = open
    ? MINUTES_PER_DAY
    : minutesIntoDay(session.clockOutAt as string) + endsDaysLater * MINUTES_PER_DAY;

  const hours = open ? 0 : hoursBetween(session.clockInAt, session.clockOutAt as string);
  const suspect = !open && hours >= IMPLAUSIBLE_HOURS;

  const offset = Math.min(Math.max(startMin / MINUTES_PER_DAY, 0), 1);
  // CLAMPED AT MIDNIGHT: the track IS one day, so the part after midnight is stated in
  // the time row ("→ 2:03a · overnight") rather than drawn off the end of the card.
  const width = Math.max(Math.min(endMin, MINUTES_PER_DAY) / MINUTES_PER_DAY - offset, 0.01);

  return {
    session,
    ordinal,
    offset,
    width,
    hours,
    open,
    suspect,
    endsDaysLater,
    status: statusOf(session, open, suspect),
  };
}

/**
 * Collapse every entry's flags into one deduplicated row for the card.
 *
 * 🔥 Michael, 2026-09-30: "If a flag is for all or multiple entries on a card, it
 * should only make one circle so we don't have duplicate flags." A day with three
 * entries all missing an earning type used to show three identical red dots, which
 * reads as three different problems and is one.
 *
 * WHICH ENTRIES CARRY IT IS KEPT and named in the tooltip, because the dedup would
 * otherwise throw away the only thing that makes a flag actionable on a split day —
 * "Entry 2" tells the reviewer which arrow to press in the dialog.
 *
 * `frozen` is dropped: every posted session carries it, so as a dot it would be noise.
 * Posting is reported by the post dot instead, which says whether it actually landed.
 */
export function cardFlags(
  entries: readonly EntryBar[],
  presenceBySession: ReadonlyMap<string, string> | undefined
): CardFlag[] {
  const byKey = new Map<string, CardFlag>();
  const add = (key: string, flag: Omit<CardFlag, 'key' | 'entries'>, ordinal: number): void => {
    const existing = byKey.get(key);
    if (existing === undefined) {
      byKey.set(key, { key, ...flag, entries: [ordinal] });
      return;
    }
    existing.entries.push(ordinal);
  };

  for (const bar of entries) {
    if (presenceBySession?.has(bar.session.id) === true) {
      add(
        'presence',
        {
          label: 'Someone else is here',
          description: presenceBySession.get(bar.session.id) ?? '',
          severity: 'presence',
        },
        bar.ordinal
      );
    }
    for (const f of deriveSessionFlags(bar.session)) {
      if (f.kind === 'frozen') {
        continue;
      }
      // Keyed by KIND AND WORDING. Two long-shift flags with different hour counts are
      // the same concern but not the same sentence, and merging them would show one
      // tooltip that is wrong about one of the entries it claims to cover.
      add(
        `${f.kind}:${f.label}`,
        { label: f.label, description: f.description, severity: f.severity },
        bar.ordinal
      );
    }
    const post =
      bar.session.postStatus === undefined
        ? null
        : postOutcomeBadge(bar.session.postStatus, bar.session.postMessage);
    if (post !== null) {
      add(
        `post:${post.label}:${post.tooltip}`,
        {
          label: `Post result: ${post.label}`,
          description: post.tooltip,
          severity:
            post.color === 'success' ? 'success' : post.color === 'danger' ? 'danger' : 'warning',
        },
        bar.ordinal
      );
    }
  }
  return [...byKey.values()];
}

/** "Entry 2", "Entries 1 & 3", "All 3 entries" — which stretches a flag concerns. */
export function entryScope(entries: readonly number[], total: number): string {
  if (total === 1) {
    return '';
  }
  if (entries.length === total) {
    return `All ${String(total)} entries`;
  }
  if (entries.length === 1) {
    return `Entry ${String(entries[0] ?? 1)}`;
  }
  return `Entries ${entries.join(' & ')}`;
}

export function WeekCalendar({
  blocks,
  days,
  supervisorNameByEmployee,
  departmentNameById,
  presenceBySession,
  onSelect,
  selectedIds,
  onToggleSelect,
  onSetSelected,
  isApprovable,
}: WeekCalendarProps): JSX.Element {
  const styles = useStyles();

  const dayLabels = useMemo(
    () =>
      days.map((d) => {
        // Parsed as local noon so the label never slips a day across a timezone.
        const date = new Date(`${d}T12:00:00`);
        return {
          key: d,
          label: date.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'numeric',
            day: 'numeric',
          }),
          weekend: date.getDay() === 0 || date.getDay() === 6,
        };
      }),
    [days]
  );

  /**
   * Every employee's week, as one card per day they worked.
   *
   * Built once per data change rather than inline in the render: an employee row
   * rebuilding its cards on every keystroke elsewhere on the page was measurable, and
   * this view is shown for ~90 people at a time.
   */
  const cardsByEmployee = useMemo(() => {
    const out = new Map<string, DayCard[]>();
    for (const block of blocks) {
      const byDay = new Map<number, TimeSession[]>();
      for (const session of block.sessions) {
        const dayIndex = days.indexOf(dayKey(session.clockInAt));
        if (dayIndex === -1) {
          continue;
        }
        const bucket = byDay.get(dayIndex);
        if (bucket === undefined) {
          byDay.set(dayIndex, [session]);
        } else {
          bucket.push(session);
        }
      }

      const cards: DayCard[] = [];
      for (const [dayIndex, sessions] of byDay) {
        // Chronological, so "Entry 1" is the first stretch of the day for everybody.
        // The dialog's arrows and the card's rows have to agree about the numbering or
        // a flag tooltip points at the wrong stretch.
        const ordered = [...sessions].sort(
          (a, b) => Date.parse(a.clockInAt) - Date.parse(b.clockInAt)
        );
        const entries = ordered.map((s, i) => entryBar(s, i + 1));

        const hoursByDept = new Map<string, number>();
        for (const bar of entries) {
          hoursByDept.set(
            bar.session.departmentId,
            (hoursByDept.get(bar.session.departmentId) ?? 0) + bar.hours
          );
        }
        const deptIds = [...hoursByDept.keys()];
        const soleDeptId = deptIds[0];
        cards.push({
          dayIndex,
          entries,
          totalHours: entries.reduce((sum, b) => sum + b.hours, 0),
          // Only when the day actually split. One department name repeated under the
          // total is a row that says nothing and costs every card its height.
          deptSplit:
            deptIds.length > 1
              ? deptIds
                  .map((id) => ({
                    name: departmentNameById.get(id) ?? 'Unknown department',
                    hours: hoursByDept.get(id) ?? 0,
                  }))
                  .sort((a, b) => (a.name < b.name ? -1 : 1))
              : [],
          soleDeptName:
            deptIds.length === 1 && soleDeptId !== undefined
              ? (departmentNameById.get(soleDeptId) ?? 'Unknown department')
              : null,
          flags: cardFlags(entries, presenceBySession),
        });
      }
      out.set(
        block.employeeId,
        cards.sort((a, b) => a.dayIndex - b.dayIndex)
      );
    }
    return out;
  }, [blocks, days, departmentNameById, presenceBySession]);

  const dotClass = (severity: CardFlag['severity']): string => {
    switch (severity) {
      case 'danger':
        return `${styles.dot} ${styles.dotDanger}`;
      case 'warning':
        return `${styles.dot} ${styles.dotWarning}`;
      case 'presence':
        return `${styles.dot} ${styles.dotPresence}`;
      case 'success':
        return `${styles.dot} ${styles.dotSuccess}`;
      default:
        return `${styles.dot} ${styles.dotImportant}`;
    }
  };

  if (blocks.length === 0) {
    return <div className={styles.empty}>No time entries in this period.</div>;
  }

  return (
    <>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.legendStrip}>
            <span
              className={styles.stripFill}
              style={{ left: '18%', width: '20%', backgroundColor: BAR_COLOR.pending }}
            />
            <span
              className={styles.stripFill}
              style={{ left: '52%', width: '28%', backgroundColor: BAR_COLOR.approved }}
            />
          </span>
          one bar per entry on a midnight-to-midnight day — the gap is the break
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatch} style={{ backgroundColor: BAR_COLOR.pending }} />
          awaiting approval
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatch} style={{ backgroundColor: BAR_COLOR.approved }} />
          approved
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatch} style={{ backgroundColor: BAR_COLOR.posted }} />
          posted
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatch} style={{ backgroundColor: BAR_COLOR.open }} />
          still clocked in
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatch} style={{ backgroundColor: BAR_COLOR.suspect }} />
          needs looking at
        </span>
      </div>
      <div className={styles.grid} role="table" aria-label="Time entries for the payroll week">
        <div className={styles.headCell} role="columnheader">
          Employee
        </div>
        {dayLabels.map((d) => (
          <div
            key={d.key}
            className={d.weekend ? `${styles.headCell} ${styles.headWeekend}` : styles.headCell}
            role="columnheader"
          >
            {d.label}
          </div>
        ))}

        {blocks.map((block) => {
          const cards = cardsByEmployee.get(block.employeeId) ?? [];
          const supervisor = supervisorNameByEmployee.get(block.employeeId);
          return (
            <Fragment key={block.employeeId}>
              <div className={styles.nameCell} role="rowheader">
                <span className={styles.empName}>{block.employeeName}</span>
                {/* Michael's 2026-09-17 #6: who is accountable, without opening anything. */}
                <span className={styles.supervisor}>{supervisor ?? 'No supervisor'}</span>
                {/*
                  * The WEEK split by department, and the totals under it.
                  *
                  * UNAPPROVED IS THE FIGURE THAT MATTERS, so it is the one in red and
                  * the one shown even when zero — "0.00 unapproved" is the answer
                  * somebody is looking for when they are chasing a week closed, and an
                  * absent number would read as "not calculated" rather than "nothing
                  * left".
                  */}
                {block.deptTotals.length > 0 ? (
                  <div className={styles.deptSplit}>
                    {block.deptTotals.map((t) => {
                      const total = t.approvedHours + t.unapprovedHours;
                      return (
                        <Tooltip
                          key={t.departmentId}
                          content={
                            `${t.departmentName}: ${t.approvedHours.toFixed(2)}h approved, ` +
                            `${t.unapprovedHours.toFixed(2)}h unapproved`
                          }
                          relationship="label"
                        >
                          <div className={styles.deptRow}>
                            <span className={styles.deptName}>{t.departmentName}</span>
                            <span
                              className={
                                t.unapprovedHours > 0
                                  ? `${styles.deptHours} ${styles.deptUnapproved}`
                                  : styles.deptHours
                              }
                            >
                              {total.toFixed(2)}
                            </span>
                          </div>
                        </Tooltip>
                      );
                    })}
                    <div className={styles.deptTotalRow}>
                      <span className={styles.deptName}>Total</span>
                      <span className={styles.deptHours}>
                        {block.totalUnapproved > 0 ? (
                          <span className={styles.deptUnapproved}>
                            {block.totalUnapproved.toFixed(2)}
                          </span>
                        ) : null}
                        {block.totalUnapproved > 0 ? ' · ' : ''}
                        {(block.totalApproved + block.totalUnapproved).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>

              {dayLabels.map((d, dayIndex) => {
                const card = cards.find((c) => c.dayIndex === dayIndex);
                const first = card?.entries[0];
                /*
                 * Which entries on this day can be ticked, and how the card-level box
                 * should read.
                 *
                 * APPROVABILITY IS THE CALLER'S ANSWER (`isApprovable`) and nothing here
                 * second-guesses it — in particular A FLAGGED ENTRY IS STILL APPROVABLE.
                 * Long shifts, back-to-back pairs and missed clock-outs are flags, not
                 * blocks: "they should still be able to be approved and thus should also
                 * be able to be selected" (Michael, 2026-09-17).
                 */
                const approvableHere = (card?.entries ?? []).filter(
                  (b) => onToggleSelect !== undefined && (isApprovable?.(b.session) ?? false)
                );
                const selectedHere = approvableHere.filter(
                  (b) => selectedIds?.has(b.session.id) === true
                );
                const allHereSelected =
                  approvableHere.length > 0 && selectedHere.length === approvableHere.length;
                const someHereSelected = selectedHere.length > 0 && !allHereSelected;
                // Two or more, or it is the row below it wearing a hat — and it needs
                // `onSetSelected`, since ticking many at once is the only thing it does.
                const showCardSelect = approvableHere.length > 1 && onSetSelected !== undefined;
                return (
                  <div
                    key={d.key}
                    className={
                      d.weekend ? `${styles.dayCell} ${styles.weekendCell}` : styles.dayCell
                    }
                  >
                    {card === undefined || first === undefined ? null : (
                      <div
                        className={styles.card}
                        style={{ background: washFor(card.entries) }}
                        onClick={() => {
                          onSelect?.(
                            first.session,
                            card.entries.map((e) => e.session)
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelect?.(
                              first.session,
                              card.entries.map((x) => x.session)
                            );
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={
                          `${block.employeeName}, ${d.label}: ` +
                          `${String(card.entries.length)} ${card.entries.length === 1 ? 'entry' : 'entries'}, ` +
                          `${card.totalHours.toFixed(2)} hours`
                        }
                      >
                        {/*
                          * 1.iv — the day's combined total, now the card's FIRST line, and
                          * the line the card-level "All" box rides.
                          *
                          * 🔥 THE "All" BOX STAYS ON A SHARED ROW. It had one of its own
                          * for about an hour and Michael's verdict was "It looks rediculous
                          * like this" — a card is ~92px and that spent a ninth of it on one
                          * control with nothing beside it. `marginLeft: auto` parks it at
                          * the right end of whichever line leads the card, so it costs no
                          * height. That line used to be the first set of times; now it is
                          * the total.
                          */}
                        <div className={styles.totalRow}>
                          <span className={styles.totalText}>
                            {card.entries.some((b) => b.open)
                              ? `${card.totalHours.toFixed(2)} hours · still in`
                              : `${card.totalHours.toFixed(2)} hours`}
                          </span>
                          {showCardSelect ? (
                            <span
                              className={`${styles.checkAll} ${styles.selectAllInline}`}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                              }}
                              role="presentation"
                            >
                              <Tooltip
                                content={`Select all ${String(approvableHere.length)} entries on this day for approval`}
                                relationship="label"
                              >
                                <span>
                                  <Checkbox
                                    checked={
                                      allHereSelected ? true : someHereSelected ? 'mixed' : false
                                    }
                                    label="All"
                                    aria-label={`Select all ${String(approvableHere.length)} entries on ${d.label} for approval`}
                                    onChange={() => {
                                      onSetSelected?.(
                                        approvableHere.map((b) => b.session.id),
                                        !allHereSelected
                                      );
                                    }}
                                  />
                                </span>
                              </Tooltip>
                            </span>
                          ) : null}
                        </div>
                        {card.deptSplit.map((split) => (
                          <div key={split.name} className={styles.cardDeptRow}>
                            <span className={styles.cardDeptName}>{split.name}</span>
                            <span className={styles.cardDeptHours}>{split.hours.toFixed(2)}</span>
                          </div>
                        ))}
                        {card.soleDeptName !== null ? (
                          <Tooltip content={card.soleDeptName} relationship="label">
                            <div className={styles.soleDept}>{card.soleDeptName}</div>
                          </Tooltip>
                        ) : null}

                        {/*
                          * 1.iii — the times, one row per entry, LAST IN THE HEADER.
                          *
                          * They led the card until Michael looked at them beside their
                          * checkboxes (2026-09-30): "since the times are kind of indented
                          * with the checkboxes, it would look better if they were the last
                          * thing in the card headers." The indent a checkbox forces reads
                          * as a sub-item, and a sub-item is the wrong thing to open a card
                          * with — the day's TOTAL is the summary, and the stretches that
                          * make it up belong under it.
                          */}
                        {card.entries.map((bar) => {
                          const canTick =
                            onToggleSelect !== undefined && (isApprovable?.(bar.session) ?? false);
                          const text = bar.open
                            ? `${shortTime(bar.session.clockInAt)} · still in`
                            : bar.endsDaysLater > 0
                              ? `${shortTime(bar.session.clockInAt)} → ${shortTime(bar.session.clockOutAt as string)}`
                              : `${shortTime(bar.session.clockInAt)}–${shortTime(bar.session.clockOutAt as string)}`;
                          const suffix =
                            bar.endsDaysLater === 1
                              ? ' · overnight'
                              : bar.endsDaysLater > 1
                                ? ` · +${String(bar.endsDaysLater)} days`
                                : '';
                          return (
                            <div
                              key={bar.session.id}
                              className={
                                bar.suspect
                                  ? `${styles.timeRow} ${styles.timeRowSuspect}`
                                  : bar.open
                                    ? `${styles.timeRow} ${styles.timeRowOpen}`
                                    : styles.timeRow
                              }
                            >
                              {canTick ? (
                                <span
                                  className={styles.check}
                                  onClick={(e) => {
                                    // The card behind this is a button. Without stopping
                                    // here, ticking a box also opens the dialog on top of
                                    // the day the reviewer is working through.
                                    e.stopPropagation();
                                  }}
                                  onKeyDown={(e) => {
                                    e.stopPropagation();
                                  }}
                                  role="presentation"
                                >
                                  <Checkbox
                                    checked={selectedIds?.has(bar.session.id) ?? false}
                                    aria-label={`Select ${text}${suffix} for approval`}
                                    onChange={() => {
                                      onToggleSelect(bar.session.id);
                                    }}
                                  />
                                </span>
                              ) : (
                                <span className={styles.checkSpacer} aria-hidden="true" />
                              )}
                              <span
                                className={styles.timeText}
                                onClick={(e) => {
                                  /*
                                   * Opens THIS entry, not the day's first one. The flag
                                   * dots below say "Entry 2", and until now the only way
                                   * to reach entry 2 was to open entry 1 and press an
                                   * arrow — an instruction the card gave and could not
                                   * itself obey.
                                   */
                                  e.stopPropagation();
                                  onSelect?.(
                                    bar.session,
                                    card.entries.map((x) => x.session)
                                  );
                                }}
                                role="presentation"
                              >
                                {bar.suspect ? '⚠ ' : ''}
                                {text}
                                {suffix}
                              </span>
                            </div>
                          );
                        })}

                        {/*
                          * The header ends here. Everything above stays packed at the
                          * top; this absorbs whatever extra height the tallest card in
                          * the row handed this one, so the dots and the bar below land
                          * on the bottom edge of every card alike.
                          */}
                        <div className={styles.cardSpacer} />

                        {/* 1.v — every flag on the day, once each, bottom right. */}
                        <div className={styles.dots}>
                          {card.flags.map((f) => {
                            const scope = entryScope(f.entries, card.entries.length);
                            const text =
                              scope === ''
                                ? `${f.label} — ${f.description}`
                                : `${scope} · ${f.label} — ${f.description}`;
                            return (
                              <Tooltip key={f.key} content={text} relationship="label">
                                <span
                                  className={dotClass(f.severity)}
                                  aria-label={text}
                                  role="img"
                                  tabIndex={0}
                                />
                              </Tooltip>
                            );
                          })}
                        </div>

                        {/* 1.i — one track, one fill per entry, gaps where the breaks were. */}
                        <div className={styles.strip}>
                          {card.entries.map((bar) => (
                            <div
                              key={bar.session.id}
                              className={styles.stripFill}
                              style={{
                                left: `${String(bar.offset * 100)}%`,
                                // A floor in PIXELS, not percent: 1% of a narrow column
                                // is sub-pixel. A short shift should read as a short
                                // bar, never vanish entirely.
                                width: `max(${String(bar.width * 100)}%, 4px)`,
                                backgroundColor: BAR_COLOR[bar.status],
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </>
  );
}
